// src/pages/happinessreport/components/IndicatorsRow.tsx
/**
 * Renderiza os PercentCard com indicadores principais.
 * Recebe os dados gerais e filtrados e decide o que exibir (filtrado ou geral).
 */

import React from 'react';
import PercentCard from '../../../components/dashboard/percentCard';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ContainerPercentCard } from '../../../components/dashboard/percentCard/styles';
import { useTranslation } from 'react-i18next';

const theme = createTheme({
  components: {
    MuiTooltip: { styleOverrides: { tooltip: { fontSize: '.9em' } } }
  }
});

type Props = {
  loading: boolean;
  dateValue: string;
  companyCreatedAt?: string | null;
  generalData: any;
  filteredData: any;
  hasFilter: boolean;
};

type YearMonth = {
  year: number;
  month: number;
};

type IndicatorKey = 'ifoz' | 'iap' | 'iep' | 'ifcz';

const parseYearMonth = (value?: string | null): YearMonth | null => {
  if (!value) {
    return null;
  }

  const isoMatch = /^(\d{4})-(\d{2})/.exec(value);
  if (isoMatch) {
    return {
      year: Number(isoMatch[1]),
      month: Number(isoMatch[2])
    };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return {
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1
  };
};

const isBeforeMonth = (left: YearMonth, right: YearMonth) =>
  left.year < right.year || (left.year === right.year && left.month < right.month);

const isAfterMonth = (left: YearMonth, right: YearMonth) =>
  left.year > right.year || (left.year === right.year && left.month > right.month);

const getCurrentSystemYearMonth = (): YearMonth => {
  const referenceDate = new Date();
  const currentMonth = referenceDate.getMonth() + 1;

  if (referenceDate.getDate() < 20) {
    if (currentMonth === 1) {
      return {
        year: referenceDate.getFullYear() - 1,
        month: 12
      };
    }

    return {
      year: referenceDate.getFullYear(),
      month: currentMonth - 1
    };
  }

  return {
    year: referenceDate.getFullYear(),
    month: currentMonth
  };
};

const isValidNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

const isPositiveNumber = (value: number | null | undefined): value is number =>
  isValidNumber(value) && value > 0;

const resolveIndicatorValue = (
  indicatorKey: IndicatorKey,
  selectedMonth: YearMonth,
  companyCreatedYearMonth: YearMonth | null,
  currentYearMonth: YearMonth,
  ifozValue: number | null,
  iapValue: number | null,
  iepValue: number | null,
  ifczValue: number | null
) => {
  const hasValidIfoz = isValidNumber(ifozValue);
  const hasPositiveIap = isPositiveNumber(iapValue);
  const hasReleaseForMonth = hasValidIfoz || hasPositiveIap;
  const shouldBypassDateRestrictions = hasValidIfoz;

  if (companyCreatedYearMonth && isBeforeMonth(selectedMonth, companyCreatedYearMonth) && !shouldBypassDateRestrictions) {
    return null;
  }

  if (isAfterMonth(selectedMonth, currentYearMonth) && !hasReleaseForMonth && !shouldBypassDateRestrictions) {
    return null;
  }

  switch (indicatorKey) {
    case 'ifoz':
      return hasValidIfoz ? ifozValue : null;
    case 'iap':
      return hasValidIfoz || hasPositiveIap ? iapValue : null;
    case 'iep':
      return hasValidIfoz || hasPositiveIap ? iepValue : null;
    case 'ifcz':
      return hasValidIfoz || hasPositiveIap ? ifczValue : null;
    default:
      return null;
  }
};

const IndicatorsRow: React.FC<Props> = ({ loading, dateValue, companyCreatedAt, generalData, filteredData, hasFilter }) => {
  const { t } = useTranslation('reports');
  const { t: ti } = useTranslation('insights');
  const getValue = (filteredValue: any, generalValue: any) => (hasFilter ? (filteredValue ?? null) : (generalValue ?? null));
  const selectedMonth = parseYearMonth(dateValue) ?? getCurrentSystemYearMonth();
  const companyCreatedYearMonth = parseYearMonth(companyCreatedAt);
  const currentYearMonth = getCurrentSystemYearMonth();

  const resolvedIfoz = getValue(filteredData?.filteredIfoz, generalData?.generalIfoz);
  const resolvedIap = getValue(filteredData?.filteredIap, generalData?.generalIap);
  const resolvedIep = getValue(filteredData?.filteredIep, generalData?.generalIep);
  const resolvedIfcz = getValue(filteredData?.filteredIfcz, generalData?.generalIfcz);

  const visibleIfoz = resolveIndicatorValue('ifoz', selectedMonth, companyCreatedYearMonth, currentYearMonth, resolvedIfoz, resolvedIap, resolvedIep, resolvedIfcz);
  const visibleIap = resolveIndicatorValue('iap', selectedMonth, companyCreatedYearMonth, currentYearMonth, resolvedIfoz, resolvedIap, resolvedIep, resolvedIfcz);
  const visibleIep = resolveIndicatorValue('iep', selectedMonth, companyCreatedYearMonth, currentYearMonth, resolvedIfoz, resolvedIap, resolvedIep, resolvedIfcz);
  const visibleIfcz = resolveIndicatorValue('ifcz', selectedMonth, companyCreatedYearMonth, currentYearMonth, resolvedIfoz, resolvedIap, resolvedIep, resolvedIfcz);

  if (loading) {
    return null;
  }

  return (
    <ContainerPercentCard>
      <ThemeProvider theme={theme}>
        <PercentCard
          title={ti('indicator.ifoz')}
          tooltipText={t('indicatorTooltip.ifoz')}
          percentage={visibleIfoz}
          filterTag={hasFilter ? ti('indicatorFilter') : null}
          noDataTooltip={String(ti('shared.minRespondents'))}
        />
        <PercentCard
          title={ti('indicator.iap')}
          tooltipText={t('indicatorTooltip.iap')}
          percentage={visibleIap}
          filterTag={hasFilter ? ti('indicatorFilter') : null}
          noDataTooltip={String(ti('shared.noReflection'))}
        />
        <PercentCard
          title={ti('indicator.iep')}
          tooltipText={t('indicatorTooltip.iep')}
          percentage={visibleIep}
          filterTag={hasFilter ? ti('indicatorFilter') : null}
          noDataTooltip={String(ti('shared.threeMonths'))}
        />
        <PercentCard
          title={ti('indicator.ifcz')}
          tooltipText={t('indicatorTooltip.ifcz')}
          percentage={visibleIfcz}
          filterTag={hasFilter ? ti('indicatorFilter') : null}
          noDataTooltip={String(ti('shared.threeMonths'))}
        />
      </ThemeProvider>
    </ContainerPercentCard>
  );
};

export default IndicatorsRow;
